/** Template knobs on the chooser: pure helpers over a template's params_schema and presets (vp/render/presets.py). */
import type { ParamSpec, PresetOut, TemplateOut, TryOut } from "../types";
import { asCanvas, asPalette } from "./clipLanes";

export type Params = Record<string, unknown>;
export type ParamsByTemplate = Record<string, Params>;

export const GROUP_LABELS: Record<string, string> = {
  caption: "Caption", motion: "Motion", plate: "Plate", variant: "Variant", locked: "Locked (always)",
};

/** Editable knobs (not the preset picker, not locked), grouped in schema order. */
export function groups(schema: readonly ParamSpec[]): { group: string; specs: ParamSpec[] }[] {
  const out: { group: string; specs: ParamSpec[] }[] = [];
  for (const s of schema) {
    if (s.key === "preset") continue;
    const g = s.locked ? "locked" : s.group;
    let row = out.find((x) => x.group === g);
    if (!row) {
      row = { group: g, specs: [] };
      out.push(row);
    }
    row.specs.push(s);
  }
  return out;
}

export function editable(t: Pick<TemplateOut, "params_schema">): boolean {
  return t.params_schema.some((s) => !s.locked);
}

/** The preset a render of these params uses: the explicit one, else the clip's default (artist's version). */
export function presetId(params: Params, defaults: Params): string | null {
  const p = params.preset ?? defaults.preset;
  return typeof p === "string" && p ? p : null;
}

/** Every knob value shown in the editor: schema defaults ⊕ the preset's values ⊕ the user's overrides. */
export function effective(schema: readonly ParamSpec[], presets: readonly PresetOut[], params: Params,
  defaults: Params): Params {
  const out: Params = {};
  for (const s of schema) if (s.key !== "preset") out[s.key] = s.default;
  const pid = presetId(params, defaults);
  const preset = presets.find((p) => p.id === pid);
  if (preset) Object.assign(out, preset.params);
  for (const [k, v] of Object.entries(params)) if (k !== "preset") out[k] = v;
  if (pid) out.preset = pid;
  return out;
}

/** Overrides only: values equal to the preset's (or the schema default) are dropped, so the request stays small. */
export function overrides(schema: readonly ParamSpec[], presets: readonly PresetOut[], params: Params,
  defaults: Params): Params {
  const base = effective(schema, presets, params.preset ? { preset: params.preset } : {}, defaults);
  const out: Params = {};
  if (typeof params.preset === "string" && params.preset) out.preset = params.preset;
  for (const [k, v] of Object.entries(params)) {
    if (k === "preset") continue;
    if (stable(v) !== stable(base[k])) out[k] = v;
  }
  return out;
}

/** Deterministic JSON (sorted keys): idempotency keys and change detection. */
export function stable(v: unknown): string {
  if (Array.isArray(v)) return `[${v.map(stable).join(",")}]`;
  if (v && typeof v === "object") {
    return `{${Object.keys(v as object).sort().map((k) => `${JSON.stringify(k)}:${stable((v as Params)[k])}`).join(",")}}`;
  }
  return JSON.stringify(v ?? null);
}

/** Clamp/round a typed number to the knob's range and step. */
export function coerce(spec: ParamSpec, raw: string): number | null {
  const n = Number(raw);
  if (raw.trim() === "" || !Number.isFinite(n)) return null;
  let v = n;
  if (spec.min != null) v = Math.max(spec.min, v);
  if (spec.max != null) v = Math.min(spec.max, v);
  if (spec.type === "int") v = Math.round(v);
  return Number(v.toFixed(4));
}

export function displayValue(v: unknown): string {
  if (Array.isArray(v)) return v.join(", ");
  if (typeof v === "boolean") return v ? "on" : "off";
  if (v == null) return "—";
  return String(v);
}

/** Knob value the server resolves per clip (plate_height / plate_x / plate_y / palette: canvas + palette solvers). */
export const AUTO = "auto";

/** A fresh random `seed` param (a new variant: canvas position ±1.5 %, swing, blur). */
export function newSeed(rand: () => number = Math.random): number {
  return 1 + Math.floor(rand() * 2_147_483_646);
}

/** What the `auto` knobs (and the seed) resolved to in a try, as display text per param key. */
export function resolvedValues(t: Pick<TryOut, "canvas" | "palette" | "seed"> | null | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  const cv = asCanvas(t?.canvas);
  if (cv) {
    out.plate_height = `${(cv.plate_pct ?? (cv.plate_h_px / 1920) * 100).toFixed(1)}% (${cv.plate_h_px} px)`;
    out.plate_x = cv.crop_x.toFixed(3);
    out.plate_y = cv.plate_cy.toFixed(3);
  }
  const p = asPalette(t?.palette);
  if (p) out.palette = `${p.colours.join(", ")}${p.stroke ? " + black stroke" : ""}${p.mix_id ? ` (${p.mix_id})` : ""}`;
  if (t?.seed != null) out.seed = String(t.seed);
  return out;
}

/** A number knob that also takes `auto` (plate height / pan / centre y): the schema default is `auto`. */
export function autoNumber(spec: ParamSpec): boolean {
  return (spec.type === "number" || spec.type === "int") && spec.default === AUTO;
}

/** What the canvas `auto` knobs resolved to in a try, as numbers (the start value when `auto` is switched off). */
export function resolvedNumbers(t: Pick<TryOut, "canvas"> | null | undefined): Record<string, number> {
  const cv = asCanvas(t?.canvas);
  if (!cv) return {};
  return { plate_height: Number((cv.plate_pct ?? (cv.plate_h_px / 1920) * 100).toFixed(2)), plate_x: cv.crop_x,
    plate_y: cv.plate_cy };
}

/** The number an `auto` knob starts from when switched to a number: the value resolved on this clip, else the middle
 * of its range; clamped and rounded to the knob's step. */
export function startNumber(spec: ParamSpec, resolved: number | undefined): number {
  const lo = spec.min ?? 0;
  const hi = spec.max ?? 1;
  const v = resolved ?? (lo + hi) / 2;
  const step = spec.step ?? (spec.type === "int" ? 1 : 0);
  const stepped = step > 0 ? lo + Math.round((v - lo) / step) * step : v;
  return coerce(spec, String(stepped)) ?? lo;
}

/** ChooseIn.params for the selected templates ({template_id: params}); {} when nothing was tweaked. */
export function chooseParams(selected: readonly string[], byTemplate: ParamsByTemplate): ParamsByTemplate {
  const out: ParamsByTemplate = {};
  for (const t of selected) {
    const p = byTemplate[t];
    if (p && Object.keys(p).length) out[t] = p;
  }
  return out;
}
